import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { Flex, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText } from 'styling/Typography'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { NoData } from 'components/NoData'
import { tokens } from 'styling/tokens'
import { DeliveryDisplayState, IMaterialDelivery } from 'types/materialReceipt.interface'
import { deriveState, discrepancyOf, fmt, STATES, STATE_META, taskForStation } from './materialReceiptData'
import { MaterialReceiptPage } from './MaterialReceiptShell'
import { TabScroller, TaskHint } from './MaterialReceipt.styled'

export const MaterialDeliveries = () => {
  const navigate = useNavigate()
  const { deliveries, config } = useSelector((s: RootState) => s.materialReceipt)
  const [filter, setFilter] = useState<'All' | DeliveryDisplayState>('All')

  return (
    <MaterialReceiptPage title='Deliveries'>
      {(station) => {
        const isManager = station === 'MANAGER'
        const shown = deliveries.filter((d) => filter === 'All' || deriveState(d) === filter)

        const columns: IHonestTableColumn<IMaterialDelivery>[] = [
          {
            id: 'ticket',
            header: 'Ticket',
            cell: (d) => {
              const breach = isManager && discrepancyOf(d, config.threshold)?.breach
              return (
                <Flex center gap='0.4rem'>
                  <BodyText weight='600' style={{ margin: 0 }}>{d.id}</BodyText>
                  {breach && <span title='Grade discrepancy' style={{ color: tokens.status.danger.fg }}>⚠</span>}
                </Flex>
              )
            },
          },
          { id: 'truck', header: 'Truck', cell: (d) => d.plate || '—' },
          { id: 'supplier', header: 'Supplier', cell: (d) => d.supplier },
          { id: 'source', header: 'Source', cell: (d) => d.source || '—' },
          { id: 'weights', header: 'Gross / Tare', cell: (d) => `${fmt(d.gross)} / ${fmt(d.tare)} t` },
          ...(isManager
            ? [{ id: 'grade', header: 'Grade', cell: (d: IMaterialDelivery) => (d.grade != null ? `${fmt(d.grade)} g/t` : '—') }]
            : []),
          {
            id: 'status',
            header: 'Status',
            cell: (d) => {
              const m = STATE_META[deriveState(d)]
              return <StatusPill status={m.tone}>{m.label}</StatusPill>
            },
          },
          {
            id: 'task',
            header: station === 'LAB' ? 'Your task' : 'Step',
            cell: (d) => {
              if (deriveState(d) === 'FINALIZED') return <TaskHint>Locked</TaskHint>
              const t = taskForStation(d, station)
              if (t) return <TaskHint $active>{t.label}</TaskHint>
              if (d.gross == null) return <TaskHint>Awaiting weighbridge</TaskHint>
              return <TaskHint>Awaiting other step</TaskHint>
            },
          },
        ]

        return (
          <>
            <TabScroller>
              <TabsContainer>
                {(['All', ...STATES] as const).map((s) => (
                  <Tabs key={s} active={filter === s} onClick={() => setFilter(s)}>
                    {s === 'All' ? 'All' : STATE_META[s].label}
                  </Tabs>
                ))}
              </TabsContainer>
            </TabScroller>
            {shown.length ? (
              <HonestTable
                columns={columns}
                data={shown}
                keyField='id'
                onClick={(d) => navigate(`/material-receipt/ticket/${d.id}`)}
              />
            ) : (
              <NoData text='No deliveries match this filter' />
            )}
          </>
        )
      }}
    </MaterialReceiptPage>
  )
}
